import * as Headless from '@headlessui/react';
import { useMemo } from 'react';
import { HiOutlineTableCells } from 'react-icons/hi2';

import { Checkbox, CheckboxField, CheckboxGroup } from '@/components-new/checkbox';
import { Label } from '@/components-new/fieldset';

import { Chip, ChipList, ChipValue } from '@/components/chip';

type RowPinSelectorProps = {
  tip?: string;
  label?: string;
  options: RowPinSelectorOption[];
  selected: RowPinSelectorOption[];
  onPinSelectionChange: (selected: ChipValue[]) => void;
};

export interface RowPinSelectorOption {
  label: string;
  value: string | number | readonly string[];
}

export const RowPinSelector = ({
  tip = 'Pin rows to top',
  options = [],
  selected = [],
  onPinSelectionChange,
  label = 'Pinned rows',
}: RowPinSelectorProps) => {
  const checkboxGroupValue = useMemo(
    () => selected.map((o) => o.value as string | number),
    [selected]
  );

  const handleDismiss = (id: ChipValue) => {
    const next = selected
      .filter((o) => o.value !== id)
      .map((o) => o.value);
    onPinSelectionChange(next as ChipValue[]);
  };

  const handleToggle = (optionValue: string | number) => {
    const already = checkboxGroupValue.includes(optionValue);
    const next = already
      ? checkboxGroupValue.filter((v) => v !== optionValue)
      : [...checkboxGroupValue, optionValue];
    onPinSelectionChange(next as ChipValue[]);
  };

  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-zinc-200 bg-zinc-50 px-4 py-2">
      <Headless.Popover className="relative">
        <Headless.PopoverButton
          title={tip}
          className="inline-flex items-center gap-1.5 rounded-md border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-700 shadow-sm hover:bg-zinc-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-950"
        >
          <HiOutlineTableCells className="size-3.5"/>
          {label}
        </Headless.PopoverButton>

        <Headless.PopoverPanel className="absolute left-0 z-10 mt-1 w-48 rounded-lg border border-zinc-200 bg-white p-3 shadow-lg">
          <CheckboxGroup>
            {options.map((option) => (
              <CheckboxField key={String(option.value)}>
                <Checkbox
                  checked={checkboxGroupValue.includes(option.value as string | number)}
                  onChange={() => handleToggle(option.value as string | number)}
                />
                <Label>{option.label}</Label>
              </CheckboxField>
            ))}
          </CheckboxGroup>
        </Headless.PopoverPanel>
      </Headless.Popover>

      <ChipList emptyText="No row pins selected">
        {selected.map((option) => (
          <Chip
            key={String(option.value)}
            chip={{ label: option.label, value: option.value, dismissible: true }}
            onDismiss={handleDismiss}
          />
        ))}
      </ChipList>
    </div>
  );
};
